import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ShortCode } from '@/components/data/ShortCode';
import { StatusChip } from '@/components/data/StatusChip';
import { Badge } from '@/components/ui/Badge';
import {
  orderStateSchema,
  type OrderListFilters,
  type OrderListItem,
  type OrderState,
} from '@/schemas/admin/commerce/orders';
import { ORDER_STATES, orderStateTone } from '@/features/commerce-ops/orderState';
import { useOrders } from '@/queries/commerceOps';
import { useDesigners } from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import { formatCents } from '@/lib/money';
import { useUiStore } from '@/stores/ui';

type LimitChoice = 25 | 50 | 100;
const limitChoiceSchema = z
  .number()
  .int()
  .refine((v): v is LimitChoice => v === 25 || v === 50 || v === 100);

const searchSchema = z.object({
  state: orderStateSchema.optional(),
  designer: z.string().uuid().optional(),
  limit: limitChoiceSchema.optional(),
  q: z.string().optional(),
});

type OrdersSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_authed/commerce/orders/')({
  validateSearch: searchSchema,
  component: OrdersListScreen,
});

function OrdersListScreen() {
  const { t } = useTranslation('commerce');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const designers = useDesigners();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);

  const filters: OrderListFilters = React.useMemo(() => {
    const out: OrderListFilters = { limit: search.limit ?? 25 };
    if (search.state) out.state = search.state;
    if (search.designer) out.designer = search.designer;
    return out;
  }, [search.limit, search.state, search.designer]);

  const list = useOrders(filters);

  const [draftQ, setDraftQ] = React.useState(search.q ?? '');
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      const value = draftQ.trim();
      const current = search.q ?? '';
      if (value === current) return;
      const next: OrdersSearch = { ...search, q: value || undefined };
      void navigate({ to: '/commerce/orders', search: next, replace: true });
    }, 200);
    return () => window.clearTimeout(id);
  }, [draftQ, navigate, search]);

  // Client-side short_code filter — the list endpoint doesn't accept `q`.
  const data = React.useMemo<OrderListItem[]>(() => {
    const all = list.data ?? [];
    const needle = (search.q ?? '').trim().toUpperCase();
    if (!needle) return all;
    return all.filter((o) => o.short_code.toUpperCase().includes(needle));
  }, [list.data, search.q]);

  function setSearchParam<K extends keyof OrdersSearch>(key: K, value: OrdersSearch[K]) {
    const next: OrdersSearch = { ...search, [key]: value };
    void navigate({ to: '/commerce/orders', search: next, replace: true });
  }

  const columns: readonly DataColumn<OrderListItem>[] = [
    {
      id: 'short_code',
      header: t('orders.table.shortCode'),
      cell: (o) => <ShortCode code={o.short_code} to={`/commerce/orders/${o.id}`} />,
      sortBy: (o) => o.short_code,
    },
    {
      id: 'total',
      header: t('orders.table.total'),
      align: 'end',
      cell: (o) => (
        <span dir="ltr" className="font-mono">
          {formatCents(o.total_cents, locale, o.currency)}
        </span>
      ),
      sortBy: (o) => o.total_cents,
    },
    {
      id: 'state',
      header: t('orders.table.state'),
      cell: (o) => (
        <Badge tone={orderStateTone(o.state)}>{t(`orders.states.${o.state}`)}</Badge>
      ),
      sortBy: (o) => o.state,
    },
    {
      id: 'placedAt',
      header: t('orders.table.placedAt'),
      cell: (o) => <DateTime value={o.placed_at ?? o.created_at ?? null} />,
      sortBy: (o) => (o.placed_at ?? o.created_at ?? null) ?? '',
    },
  ];
  void StatusChip; // referenced via dynamic state-chip helper

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('orders.title')}</h1>
        <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('orders.subtitle')}</p>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="grow basis-64">
              <label htmlFor="orders-q" className="sr-only">
                {t('orders.filters.search')}
              </label>
              <Input
                id="orders-q"
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
                placeholder={t('orders.filters.search')}
                autoComplete="off"
                dir="ltr"
              />
            </div>
            <div className="basis-56">
              <label htmlFor="orders-state" className="sr-only">
                {t('orders.table.state')}
              </label>
              <select
                id="orders-state"
                value={search.state ?? ''}
                onChange={(e) =>
                  setSearchParam('state', (e.target.value || undefined) as OrderState | undefined)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('orders.filters.stateAll')}</option>
                {ORDER_STATES.map((s) => (
                  <option key={s} value={s}>
                    {t(`orders.states.${s}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="basis-56">
              <label htmlFor="orders-designer" className="sr-only">
                {t('orders.filters.designerAll')}
              </label>
              <select
                id="orders-designer"
                value={search.designer ?? ''}
                onChange={(e) =>
                  setSearchParam('designer', (e.target.value || undefined) as never)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('orders.filters.designerAll')}</option>
                {(designers.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="basis-32">
              <label htmlFor="orders-limit" className="sr-only">
                {t('orders.filters.limit')}
              </label>
              <select
                id="orders-limit"
                value={search.limit ?? 25}
                onChange={(e) =>
                  setSearchParam('limit', Number(e.target.value) as 25 | 50 | 100)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <DataTable<OrderListItem>
            data={data}
            columns={columns}
            rowKey={(o) => o.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: t('orders.empty.title'),
              body: t('orders.empty.body'),
            }}
            initialSort={{ id: 'placedAt', direction: 'desc' }}
            onRowClick={(o) =>
              void navigate({ to: '/commerce/orders/$id', params: { id: o.id } })
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
